// Pure colour maths for ctx.charts. No DOM, no imports — unit-tested via
// `node --test` and consumed from TypeScript under Vite.
// KEEP IN SYNC with ./charts-color.d.mts, which hand-mirrors these signatures
// for the strict shell tsconfig (no allowJs).
//
// The one non-obvious function here is `modeForSurface`. The library picks its
// palette from the *live* surface colour rather than a hardcoded mode, because
// ArcLog ships a dark-first shell AND a light `theme-windows-classic`. See
// `charts-theme.ts` for where the surface comes from.

/** @typedef {{ r:number, g:number, b:number, a:number }} Rgba */

const HEX3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const HEX6 = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const HEX8 = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const FUNC = /^rgba?\(([^)]+)\)$/i;

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

/**
 * Parse `#rgb`, `#rrggbb`, `#rrggbbaa`, `rgb(...)` or `rgba(...)`.
 * Returns null for anything else (named colours, hsl(), `transparent`) — the
 * caller is expected to fall back rather than guess.
 * @param {string | null | undefined} input
 * @returns {Rgba | null}
 */
export function parseColor(input) {
  if (typeof input !== "string") return null;
  const s = input.trim();
  if (!s) return null;

  const h3 = HEX3.exec(s);
  if (h3) {
    return {
      r: parseInt(h3[1] + h3[1], 16),
      g: parseInt(h3[2] + h3[2], 16),
      b: parseInt(h3[3] + h3[3], 16),
      a: 1,
    };
  }
  const h8 = HEX8.exec(s);
  if (h8) {
    return {
      r: parseInt(h8[1], 16),
      g: parseInt(h8[2], 16),
      b: parseInt(h8[3], 16),
      a: parseInt(h8[4], 16) / 255,
    };
  }
  const h6 = HEX6.exec(s);
  if (h6) {
    return { r: parseInt(h6[1], 16), g: parseInt(h6[2], 16), b: parseInt(h6[3], 16), a: 1 };
  }

  const fn = FUNC.exec(s);
  if (fn) {
    const parts = fn[1]
      .split(/[,\s/]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length < 3) return null;
    const chan = (t) => {
      const n = parseFloat(t);
      if (!Number.isFinite(n)) return null;
      return clamp(Math.round(t.endsWith("%") ? (n / 100) * 255 : n), 0, 255);
    };
    const r = chan(parts[0]);
    const g = chan(parts[1]);
    const b = chan(parts[2]);
    if (r === null || g === null || b === null) return null;
    let a = 1;
    if (parts.length > 3) {
      const n = parseFloat(parts[3]);
      a = Number.isFinite(n) ? clamp(parts[3].endsWith("%") ? n / 100 : n, 0, 1) : 1;
    }
    return { r, g, b, a };
  }
  return null;
}

/** `{r,g,b}` → `#rrggbb`. Channels are rounded and clamped. */
export function toHex(rgb) {
  const p = (v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, "0");
  return "#" + p(rgb.r) + p(rgb.g) + p(rgb.b);
}

/** `#rrggbb` + alpha → `rgba(...)`. Unparseable input is returned unchanged. */
export function withAlpha(color, alpha) {
  const c = parseColor(color);
  if (!c) return color;
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${clamp(alpha, 0, 1)})`;
}

/** Linear blend; `t=0` → a, `t=1` → b. Alpha is ignored (opaque result). */
export function mix(a, b, t) {
  const ca = parseColor(a);
  const cb = parseColor(b);
  if (!ca || !cb) return a;
  const k = clamp(t, 0, 1);
  return toHex({
    r: ca.r + (cb.r - ca.r) * k,
    g: ca.g + (cb.g - ca.g) * k,
    b: ca.b + (cb.b - ca.b) * k,
  });
}

/**
 * Flatten a translucent colour onto an opaque backdrop. Theme tokens are often
 * `rgba(255,255,255,.55)`; luminance of that alone is meaningless.
 */
export function flatten(color, backdrop) {
  const c = parseColor(color);
  const b = parseColor(backdrop);
  if (!c) return backdrop;
  if (!b || c.a >= 1) return toHex(c);
  return toHex({
    r: b.r + (c.r - b.r) * c.a,
    g: b.g + (c.g - b.g) * c.a,
    b: b.b + (c.b - b.b) * c.a,
  });
}

/** WCAG 2.x relative luminance, 0 (black) → 1 (white). */
export function relativeLuminance(color) {
  const c = parseColor(color);
  if (!c) return 0;
  const lin = (v) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(c.r) + 0.7152 * lin(c.g) + 0.0722 * lin(c.b);
}

/** WCAG contrast ratio, 1 → 21. */
export function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Which palette a surface wants — the decision that makes charts follow the OS
 * theme instead of `prefers-color-scheme`.
 *
 * Not a luminance threshold picked by taste: the surface is "light" when BLACK
 * ink contrasts against it better than white does. That crossover sits at
 * L ≈ 0.179 and is the same rule that decides readable text colour, so the
 * palette and the labels can never disagree.
 *
 * `#0e1017` (--os-window-body) → L 0.006 → "dark".
 * `#d4d0c8` (theme-windows-classic) → L 0.65 → "light".
 * Unparseable input → "dark", matching ArcLog's dark-first default.
 * @param {string | null | undefined} surface
 * @returns {"light" | "dark"}
 */
export function modeForSurface(surface) {
  if (!parseColor(surface)) return "dark";
  return contrastRatio(surface, "#000000") >= contrastRatio(surface, "#ffffff")
    ? "light"
    : "dark";
}

/** `#rrggbb` → `{h:0-360, s:0-1, l:0-1}`. */
export function hexToHsl(color) {
  const c = parseColor(color) || { r: 0, g: 0, b: 0, a: 1 };
  const r = c.r / 255;
  const g = c.g / 255;
  const b = c.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
  else if (max === g) h = ((b - r) / d + 2) * 60;
  else h = ((r - g) / d + 4) * 60;
  return { h, s, l };
}

/** `{h,s,l}` → `#rrggbb`. */
export function hslToHex(hsl) {
  const h = ((hsl.h % 360) + 360) % 360;
  const s = clamp(hsl.s, 0, 1);
  const l = clamp(hsl.l, 0, 1);
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const seg = Math.floor(h / 60) % 6;
  const rgb = [
    [c, x, 0],
    [x, c, 0],
    [0, c, x],
    [0, x, c],
    [x, 0, c],
    [c, 0, x],
  ][seg];
  return toHex({ r: (rgb[0] + m) * 255, g: (rgb[1] + m) * 255, b: (rgb[2] + m) * 255 });
}
